import { LootProfileSchema, pickWeighted, rollEquipment, rollOneOf, rollRarity, } from '../rules/loot.js';
import { setPieceId } from './biome-sets.js';
import { BIOME_IDS, BIOMES } from './biomes.js';
import { bossOf } from './bosses.js';
import { PLAYABLE_CLASS_IDS } from './classes.js';
import { ITEMS, RARITIES } from './items.js';
import { kinOf } from './quarry-mobs.js';
import { RAID_SET } from './raid-gear.js';
import { BOUNTIES, isBountyMob } from './bounties.js';
import { isRareMob, RARE_MOBS } from './rare-mobs.js';
import { UPGRADE_MATERIALS } from './upgrade-materials.js';
/**
 * Where equipment comes from, and how often. Data, not code (CLAUDE.md rule 4).
 *
 * Every biome has a band of levels (`biomes.ts`); its pool is the lootable
 * equipment whose required level falls in that band, so a monster drops what
 * a player of its level can wear, and each place has gear of its own. The
 * sources differ only by their odds:
 *
 * - ordinary monsters: the steady trickle, rarely a piece, mostly common;
 * - the boss of a place (rare, long respawn): always a piece, rare at least;
 * - the boss of a dungeon: always a piece, epic and legendary within reach;
 * - a chest in the world: one common or rare piece, for exploring;
 * - Shefira (ADR 0068): a piece of her own set, never of a biome's pool.
 *
 * Merchants will sell the pool's commons (Phase 3, « Marchands PNJ »): nobody
 * is left stuck by bad luck.
 */
export const LOOT_SOURCES = ['ordinary', 'zone-boss', 'dungeon-boss', 'chest', 'raid'];
export const LOOT_PROFILES = Object.freeze({
    // One piece every ~35 kills: a rat pays in tails, not swords.
    ordinary: LootProfileSchema.parse({
        chance: 0.03,
        weights: { common: 70, rare: 22, epic: 7, legendary: 1 },
    }),
    'zone-boss': LootProfileSchema.parse({
        chance: 1,
        weights: { common: 0, rare: 70, epic: 25, legendary: 5 },
    }),
    'dungeon-boss': LootProfileSchema.parse({
        chance: 1,
        weights: { common: 0, rare: 40, epic: 45, legendary: 15 },
    }),
    chest: LootProfileSchema.parse({
        chance: 1,
        weights: { common: 75, rare: 25, epic: 0, legendary: 0 },
    }),
    // Once a week per character: an epic most weeks, a legendary one in six.
    raid: LootProfileSchema.parse({
        chance: 1,
        weights: { common: 0, rare: 0, epic: 85, legendary: 15 },
    }),
});
const SOURCE_OF_RANK = {
    ordinary: 'ordinary',
    'zone-boss': 'zone-boss',
    'dungeon-boss': 'dungeon-boss',
};
/** The biome whose band holds this level; past the last band, the last biome. */
export function biomeOfLevel(level) {
    const found = BIOME_IDS.find((id) => level >= BIOMES[id].level.min && level <= BIOMES[id].level.max);
    if (found !== undefined)
        return found;
    return BIOME_IDS.reduce((best, id) => BIOMES[id].level.max > BIOMES[best].level.max ? id : best);
}
/**
 * The biome a creature lives in, from its dens, its elites or its boss;
 * undefined for the peaceful and the dungeon-bound.
 */
export function biomeOfMob(mobId) {
    const boss = bossOf(mobId);
    if (boss !== undefined)
        return boss.biome;
    if (isRareMob(mobId))
        return RARE_MOBS[mobId].biome;
    if (isBountyMob(mobId))
        return BOUNTIES[mobId].biome;
    // A dragon's mark's quarry (ADR 0087) and a creature of the night (ADR 0093)
    // belong to their kin's region.
    const kin = kinOf(mobId);
    return BIOME_IDS.find((id) => BIOMES[id].dens.some((den) => den.mob === kin) ||
        Object.values(BIOMES[id].elites).includes(mobId));
}
/**
 * Only what a player can wear today: a piece for a class not yet open
 * (`PLAYABLE_CLASS_IDS`) would be a drop nobody wants.
 */
function wearable(classes) {
    return (classes.length === 0 ||
        classes.some((id) => PLAYABLE_CLASS_IDS.includes(id)));
}
function buildPool(biome) {
    const { min, max } = BIOMES[biome].level;
    const pool = Object.fromEntries(RARITIES.map((rarity) => [rarity, []]));
    for (const item of ITEMS.values()) {
        if (item.kind !== 'equipment' || !item.lootable || !wearable(item.classes))
            continue;
        if (item.levelRequired < min || item.levelRequired > max)
            continue;
        pool[item.rarity].push(item.id);
    }
    return pool;
}
export const LOOT_POOLS = Object.freeze(Object.fromEntries(BIOME_IDS.map((id) => [id, buildPool(id)])));
/** The pool a mob draws from: its biome's, or the band of its level for one without a biome. */
export function lootPoolOf(mob) {
    return LOOT_POOLS[biomeOfMob(mob.id) ?? biomeOfLevel(mob.level ?? 1)];
}
/**
 * How often a guardian of a place of interest leaves its biome's rare
 * upgrade material (ADR 0013): about fifty are needed to take a piece from
 * +5 to +8, so this is what those places are farmed for.
 */
export const GUARDIAN_RARE_CHANCE = 0.1;
/**
 * What an elite leaves (ADR 0023): not one thing at most, like the rest, but
 * a handful — pieces of its biome's gear under a profile of its own, the
 * biome's rare upgrade material, a chance at its relic (the epic material,
 * otherwise only a zone boss's), and a roll of its kin's table on top.
 */
export const ELITE_LOOT = Object.freeze({
    rare: {
        pieces: 1,
        profile: LootProfileSchema.parse({
            chance: 0.6,
            weights: { common: 0, rare: 80, epic: 18, legendary: 2 },
        }),
        rareMaterial: 1,
        relicChance: 0,
    },
    epic: {
        pieces: 1,
        profile: LootProfileSchema.parse({
            chance: 1,
            weights: { common: 0, rare: 45, epic: 45, legendary: 10 },
        }),
        rareMaterial: 2,
        relicChance: 0.25,
    },
    legendary: {
        pieces: 2,
        profile: LootProfileSchema.parse({
            chance: 1,
            weights: { common: 0, rare: 0, epic: 65, legendary: 35 },
        }),
        rareMaterial: 3,
        relicChance: 1,
    },
});
function rollEliteLoot(mob, rarity, biome, rng) {
    const loot = ELITE_LOOT[rarity];
    const drops = [];
    for (let k = 0; k < loot.pieces; k += 1) {
        const piece = rollEquipment(loot.profile, LOOT_POOLS[biome], rng);
        if (piece !== undefined)
            drops.push(piece);
    }
    drops.push({ templateId: UPGRADE_MATERIALS[biome].rare.id, quantity: loot.rareMaterial });
    if (rng() < loot.relicChance)
        drops.push({ templateId: UPGRADE_MATERIALS[biome].epic.id, quantity: 1 });
    const extra = rollOneOf(mob.loot, rng);
    if (extra !== undefined)
        drops.push(extra);
    return drops;
}
/**
 * What a zone boss leaves to each character that shares its kill (ADR 0024):
 * its biome's relic (the epic material, which opens +9 and +10), two of the
 * rare one, a piece of the biome's gear under the `zone-boss` profile, and a
 * second piece one time in three. Rolled once per share, so helping never
 * takes anything from anyone.
 */
export const BOSS_LOOT = Object.freeze({
    relic: 1,
    rareMaterial: 2,
    secondPieceChance: 1 / 3,
});
/**
 * A zone boss's trophy (ADR 0053): a cosmetic of its own that it leaves very
 * rarely, rolled on each share on top of the rest. Never in the biome's pool
 * nor in the shop: the only way to it is the boss, then trade.
 */
export const BOSS_TROPHIES = Object.freeze({
    // The frost giant's: one share in two hundred, at a kill every fifteen minutes a feat.
    snow: { templateId: 'ice-wings', quantity: 1, chance: 0.005 },
});
export function rollBossLoot(biome, rng) {
    const materials = UPGRADE_MATERIALS[biome];
    const drops = [
        { templateId: materials.epic.id, quantity: BOSS_LOOT.relic },
        { templateId: materials.rare.id, quantity: BOSS_LOOT.rareMaterial },
    ];
    const profile = LOOT_PROFILES['zone-boss'];
    const first = rollEquipment(profile, LOOT_POOLS[biome], rng);
    if (first !== undefined)
        drops.push(first);
    if (rng() < BOSS_LOOT.secondPieceChance) {
        const second = rollEquipment(profile, LOOT_POOLS[biome], rng);
        if (second !== undefined)
            drops.push(second);
    }
    const trophy = BOSS_TROPHIES[biome];
    if (trophy !== undefined && rng() < trophy.chance) {
        drops.push({ templateId: trophy.templateId, quantity: trophy.quantity });
    }
    return drops;
}
/**
 * What a wandering rare leaves to each character that shares its kill (ADR
 * 0094), rolled once per share: always a piece of its biome's gear, rare at
 * least, two of the biome's rare material, a fair chance at the relic, its
 * trophy, and a roll of its kin's table. Less than a zone boss's (no second
 * piece, no sure relic): it is found, not farmed, but never for nothing.
 */
export const RARE_MOB_LOOT = Object.freeze({
    profile: LootProfileSchema.parse({
        chance: 1,
        weights: { common: 0, rare: 55, epic: 37, legendary: 8 },
    }),
    rareMaterial: 2,
    relicChance: 0.5,
});
export function rollRareMobLoot(mob, rng) {
    const { biome, trophy } = RARE_MOBS[mob.id];
    const drops = [];
    const piece = rollEquipment(RARE_MOB_LOOT.profile, LOOT_POOLS[biome], rng);
    if (piece !== undefined)
        drops.push(piece);
    drops.push({
        templateId: UPGRADE_MATERIALS[biome].rare.id,
        quantity: RARE_MOB_LOOT.rareMaterial,
    });
    if (rng() < RARE_MOB_LOOT.relicChance)
        drops.push({ templateId: UPGRADE_MATERIALS[biome].epic.id, quantity: 1 });
    drops.push({ templateId: trophy, quantity: 1 });
    const extra = rollOneOf(mob.loot, rng);
    if (extra !== undefined)
        drops.push(extra);
    return drops;
}
/**
 * What a kill leaves: one thing at most (an elite, a handful: `ELITE_LOOT`). A single roll decides — the first
 * slice, the size of the profile's chance, is a piece of the biome's gear;
 * the rest of [0, 1) is shared by the mob's own table, then nothing. A
 * guardian first rolls for its biome's rare material; if it drops, that is
 * the one thing.
 */
export function rollMobLoot(mob, rng, options = {}) {
    const biome = biomeOfMob(mob.id);
    if (isRareMob(mob.id))
        return rollRareMobLoot({ ...mob, id: mob.id }, rng);
    if (mob.rarity !== 'common' && !mob.peaceful && biome !== undefined) {
        return rollEliteLoot(mob, mob.rarity, biome, rng);
    }
    if (options.guardian === true && !mob.peaceful && biome !== undefined) {
        if (rng() < GUARDIAN_RARE_CHANCE) {
            return [{ templateId: UPGRADE_MATERIALS[biome].rare.id, quantity: 1 }];
        }
    }
    const roll = rng();
    const profile = LOOT_PROFILES[SOURCE_OF_RANK[mob.rank]];
    const chance = mob.peaceful ? 0 : profile.chance;
    if (roll < chance) {
        const piece = rollEquipment({ ...profile, chance: 1 }, lootPoolOf(mob), rng);
        if (piece !== undefined)
            return [piece];
    }
    const drop = rollOneOf(mob.loot, rng, Math.max(0, roll - chance) / (1 - chance || 1));
    return drop === undefined ? [] : [drop];
}
/**
 * How often each piece of Shefira's set is drawn, relative to the others:
 * Ser Givrecoeur's sword three times rarer than the rest (ADR 0068).
 */
export const RAID_PIECE_WEIGHTS = Object.freeze({
    weapon: 1,
    offhand: 3,
    helmet: 3,
    chest: 3,
    gloves: 3,
    boots: 3,
});
/**
 * What Shefira leaves to each character that shares her kill in the raid,
 * once a game week: one piece of her set, its rarity under the `raid`
 * profile, the piece by `RAID_PIECE_WEIGHTS`. The weekly lock is the server's.
 */
export function rollRaidLoot(rng) {
    const rarity = rollRarity(LOOT_PROFILES.raid.weights, rng);
    const piece = pickWeighted(Object.keys(RAID_SET).map((slot) => ({
        value: RAID_SET[slot].id,
        weight: RAID_PIECE_WEIGHTS[slot],
    })), rng);
    return piece === undefined ? [] : [{ templateId: setPieceId(piece, rarity), quantity: 1 }];
}
//# sourceMappingURL=loot.js.map